/**
 * Google Generative AI Provider Implementation
 * 
 * Implements the IAIProvider interface for Google's Gemini API.
 * Supports models like Gemini 2.5, Gemini 3 Flash, Gemini 3 Pro.
 * 
 * Key differences from OpenAI:
 * - Uses API key as query parameter instead of header
 * - System instruction is passed separately from messages
 * - Uses `contents` instead of `messages`
 * - Uses `parts` array for message content
 * - Different response format with `candidates`
 */

import {
  AIProvider,
  AIModelConfig,
  AIMessage,
  AIRequestOptions,
  AIResponse
} from '../AIProvider.types';
import { BaseAIProvider } from '../BaseAIProvider';
import { GOOGLE_MODELS } from '../config/modelConfig';
import { PROVIDER_ENDPOINTS } from '../config/providerConfig';
import { ApiClient } from '../../ApiClient';

/**
 * Google API content part format
 */
interface GoogleContentPart {
  text: string;
}

/**
 * Google API content format
 */
interface GoogleContent {
  role: 'user' | 'model';
  parts: GoogleContentPart[];
}

/**
 * Google API response format
 */
interface GoogleResponse {
  candidates: Array<{
    content: {
      parts: GoogleContentPart[];
      role: string;
    };
    finishReason: string;
    index: number;
    safetyRatings?: Array<{
      category: string;
      probability: string;
    }>;
  }>;
  usageMetadata?: {
    promptTokenCount: number;
    candidatesTokenCount: number;
    totalTokenCount: number;
  };
  modelVersion?: string;
}

/**
 * Google API generation config
 */
interface GoogleGenerationConfig {
  temperature?: number;
  maxOutputTokens?: number;
  responseMimeType?: string;
  responseJsonSchema?: Record<string, unknown>;
}

/**
 * Google API request payload
 */
interface GoogleRequestPayload {
  contents: GoogleContent[];
  systemInstruction?: {
    parts: GoogleContentPart[];
  };
  generationConfig?: GoogleGenerationConfig;
}

/**
 * Google Generative AI Provider
 * 
 * Handles all communication with Google's Generative AI API, including:
 * - Chat completions with Gemini models
 * - JSON mode via responseMimeType
 * - Token management
 */
export class GoogleProvider extends BaseAIProvider {
  readonly provider = AIProvider.GOOGLE;
  
  /**
   * Get the Google API key from Forge
   */
  protected async getAuthToken(): Promise<string> {
    return await this.invokeResolver<string>('getGoogleApiToken');
  }
  
  /**
   * Generate a completion using Google's API
   */
  async generateCompletion(
    messages: AIMessage[],
    options: AIRequestOptions
  ): Promise<AIResponse> {
    this.validateRequestOptions(options);
    
    try {
      const apiKey = await this.getAuthToken();
      
      const payload = this.buildRequestPayload(messages, options);
      const endpoint = this.buildEndpoint(options.model, apiKey);
      
      const response = await ApiClient.post(
        endpoint,
        payload,
        { 'Content-Type': 'application/json' }
      ) as GoogleResponse;
      
      return this.normalizeResponse(response, options.model);
    } catch (error) {
      this.handleError(error);
    }
  }
  
  /**
   * Build the API endpoint URL with model name and API key
   * Google uses the model name in the URL path
   */
  private buildEndpoint(modelId: string, apiKey: string): string {
    const modelName = this.getApiModelName(modelId);
    return `${PROVIDER_ENDPOINTS[AIProvider.GOOGLE]}/${modelName}:generateContent?key=${apiKey}`;
  }
  
  /**
   * Get the API model name from the model ID
   */
  private getApiModelName(modelId: string): string {
    const model = GOOGLE_MODELS.find(m => m.id === modelId);
    return model?.name || modelId;
  }
  
  /**
   * Build the request payload for Google's API
   */
  private buildRequestPayload(
    messages: AIMessage[],
    options: AIRequestOptions
  ): GoogleRequestPayload {
    const { contents, systemInstruction } = this.formatMessages(messages, options.systemPrompt);
    
    const payload: GoogleRequestPayload = {
      contents
    };
    
    // Add system instruction if provided
    if (systemInstruction) {
      payload.systemInstruction = {
        parts: [{ text: systemInstruction }]
      };
    }
    
    // Build generation config
    const generationConfig: GoogleGenerationConfig = {};
    
    if (options.temperature !== undefined) {
      generationConfig.temperature = options.temperature;
    }
    
    if (options.maxTokens) {
      generationConfig.maxOutputTokens = options.maxTokens;
    }
    
    // Enable JSON mode if requested
    if (options.jsonMode) {
      generationConfig.responseMimeType = 'application/json';
      generationConfig.responseJsonSchema = {
        type: 'object',
        additionalProperties: true
      };
    }
    
    if (Object.keys(generationConfig).length > 0) {
      payload.generationConfig = generationConfig;
    }
    
    return payload;
  }
  
  /**
   * Format messages for Google's API
   * Google has specific requirements:
   * - Uses 'contents' instead of 'messages'
   * - Uses 'model' role instead of 'assistant'
   * - Uses 'parts' array for content
   * - System instruction is separate
   */
  private formatMessages(
    messages: AIMessage[],
    systemPrompt?: string
  ): { contents: GoogleContent[]; systemInstruction?: string } {
    let extractedSystemInstruction = systemPrompt || '';
    const contents: GoogleContent[] = [];
    
    // Process messages, extracting system messages
    for (const msg of this.normalizeMessages(messages)) {
      if (msg.role === 'system') {
        // Append system messages to the system instruction
        extractedSystemInstruction = extractedSystemInstruction
          ? `${extractedSystemInstruction}\n\n${msg.content}`
          : msg.content;
      } else {
        // Map roles: 'assistant' -> 'model'
        const role = msg.role === 'assistant' ? 'model' : 'user';
        contents.push({
          role,
          parts: [{ text: msg.content }]
        });
      }
    }
    
    // Google requires at least one user message
    if (contents.length === 0) {
      contents.push({
        role: 'user',
        parts: [{ text: 'Please provide a response.' }]
      });
    }
    
    // Ensure first message is from user
    if (contents[0].role !== 'user') {
      contents.unshift({
        role: 'user',
        parts: [{ text: 'Please continue.' }]
      });
    }
    
    return {
      contents,
      systemInstruction: extractedSystemInstruction || undefined
    };
  }
  
  /**
   * Normalize Google's response to our standard format
   */
  private normalizeResponse(response: GoogleResponse, modelId: string): AIResponse {
    const candidate = response.candidates?.[0];
    
    // Extract text content from parts
    const textContent = candidate?.content?.parts
      ?.filter(part => part.text)
      .map(part => part.text)
      .join('') || '';
    
    return {
      content: textContent,
      model: response.modelVersion || modelId,
      provider: this.provider,
      usage: {
        promptTokens: response.usageMetadata?.promptTokenCount || 0,
        completionTokens: response.usageMetadata?.candidatesTokenCount || 0,
        totalTokens: response.usageMetadata?.totalTokenCount || 0
      },
      finishReason: this.mapFinishReason(candidate?.finishReason)
    };
  }
  
  /**
   * Map Google's finishReason to our standard format
   */
  private mapFinishReason(finishReason?: string): string {
    switch (finishReason) {
      case 'STOP':
        return 'stop';
      case 'MAX_TOKENS':
        return 'length';
      case 'SAFETY':
        return 'content_filter';
      case 'RECITATION':
        return 'content_filter';
      case 'OTHER':
        return 'stop';
      default:
        return finishReason?.toLowerCase() || 'stop';
    }
  }
  
  /**
   * Check if Google provider is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      const apiKey = await this.getAuthToken();
      return !!apiKey && apiKey.length > 0;
    } catch {
      return false;
    }
  }
  
  /**
   * Get available Google models
   */
  getAvailableModels(): AIModelConfig[] {
    return GOOGLE_MODELS;
  }
}
